import React, { useCallback, useEffect, useRef, useState } from 'react';
import { uploadService } from '../services/api';
import { resolvePublicMediaUrl } from '../utils/newsContent';
import '../styles/RichNewsEditor.css';

type Props = {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
};

const TEXT_COLORS = [
    { label: 'Dourado', value: '#d4af37' },
    { label: 'Branco', value: '#ffffff' },
    { label: 'Cinza', value: '#b0b0b0' },
    { label: 'Vermelho', value: '#e57373' },
    { label: 'Verde', value: '#81c784' },
    { label: 'Azul', value: '#64b5f6' },
] as const;

const FONT_OPTIONS = [
    { label: 'Roboto (padrão)', value: 'Roboto, sans-serif' },
    { label: 'Cinzel (títulos)', value: 'Cinzel, serif' },
    { label: 'Marcellus', value: 'Marcellus, serif' },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Georgia', value: 'Georgia, "Times New Roman", serif' },
    { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
    { label: 'Courier New', value: '"Courier New", Courier, monospace' },
    { label: 'Verdana', value: 'Verdana, sans-serif' },
    { label: 'Tahoma', value: 'Tahoma, sans-serif' },
    { label: 'Impact', value: 'Impact, Haettenschweiler, sans-serif' },
] as const;

const SIZE_OPTIONS = [
    { label: '10px', value: '10px' },
    { label: '12px', value: '12px' },
    { label: '14px', value: '14px' },
    { label: '16px (normal)', value: '16px' },
    { label: '18px', value: '18px' },
    { label: '20px', value: '20px' },
    { label: '24px', value: '24px' },
    { label: '28px', value: '28px' },
    { label: '32px', value: '32px' },
    { label: '36px', value: '36px' },
    { label: '48px', value: '48px' },
] as const;

function escapeAttr(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function isValidLinkUrl(url: string): boolean {
    const u = url.trim();
    if (!u || /^\s*javascript:/i.test(u)) return false;
    return /^https?:\/\//i.test(u) || u.startsWith('/');
}

function youtubeEmbedUrl(raw: string): string | null {
    const url = raw.trim();
    if (!url) return null;
    const patterns = [
        /youtube\.com\/watch\?v=([\w-]+)/i,
        /youtu\.be\/([\w-]+)/i,
        /youtube\.com\/embed\/([\w-]+)/i,
        /youtube\.com\/shorts\/([\w-]+)/i,
    ];
    for (const re of patterns) {
        const m = url.match(re);
        if (m?.[1]) return `https://www.youtube.com/embed/${m[1]}`;
    }
    return null;
}

const RichNewsEditor: React.FC<Props> = ({ value, onChange, placeholder }) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const lastSynced = useRef(value);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const el = editorRef.current;
        if (!el || value === lastSynced.current) return;
        el.innerHTML = value || '';
        lastSynced.current = value;
    }, [value]);

    const emitChange = useCallback(() => {
        const html = editorRef.current?.innerHTML ?? '';
        lastSynced.current = html;
        onChange(html);
    }, [onChange]);

    const exec = (command: string, valueArg?: string) => {
        editorRef.current?.focus();
        document.execCommand(command, false, valueArg);
        emitChange();
    };

    const insertHtml = (html: string) => {
        editorRef.current?.focus();
        document.execCommand('insertHTML', false, html);
        emitChange();
    };

    const handleLink = () => {
        const url = window.prompt('URL do link:', 'https://');
        if (!url?.trim()) return;
        exec('createLink', url.trim());
    };

    const handleDownloadButton = () => {
        const label = window.prompt('Texto do botão (ex.: MIRROR 01, Download Patch):', 'Download');
        if (!label?.trim()) return;

        const url = window.prompt('URL de destino do botão:', 'https://');
        if (!url?.trim() || !isValidLinkUrl(url)) {
            setError('URL inválida. Use https://... ou um caminho começando com /');
            return;
        }
        setError(null);

        const safeLabel = escapeAttr(label.trim());
        const safeUrl = escapeAttr(url.trim());
        insertHtml(
            `<div class="news-download-buttons"><a href="${safeUrl}" class="btn-download" target="_blank" rel="noopener noreferrer">${safeLabel}</a></div><p><br></p>`
        );
    };

    /** Adiciona outro botão na última linha de botões, se existir no editor. */
    const handleDownloadButtonRow = () => {
        const label = window.prompt('Texto do botão:', 'MIRROR 02');
        if (!label?.trim()) return;
        const url = window.prompt('URL de destino:', 'https://');
        if (!url?.trim() || !isValidLinkUrl(url)) {
            setError('URL inválida.');
            return;
        }
        setError(null);

        const el = editorRef.current;
        if (!el) return;

        const row = el.querySelector('.news-download-buttons:last-of-type');
        const safeLabel = escapeAttr(label.trim());
        const safeUrl = escapeAttr(url.trim());
        const anchor = `<a href="${safeUrl}" class="btn-download" target="_blank" rel="noopener noreferrer">${safeLabel}</a>`;

        if (row) {
            row.insertAdjacentHTML('beforeend', anchor);
            emitChange();
            return;
        }

        insertHtml(
            `<div class="news-download-buttons">${anchor}</div><p><br></p>`
        );
    };

    const handleYoutube = () => {
        const url = window.prompt('Cole o link do YouTube:', 'https://www.youtube.com/watch?v=');
        const embed = url ? youtubeEmbedUrl(url) : null;
        if (!embed) {
            setError('Link do YouTube inválido.');
            return;
        }
        setError(null);
        insertHtml(
            `<div class="news-video-embed"><iframe src="${embed}" title="YouTube" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen loading="lazy"></iframe></div><p><br></p>`
        );
    };

    const applyTextColor = (color: string) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        setError(null);

        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
            document.execCommand('foreColor', false, color);
            emitChange();
            return;
        }
        applySpanStyle(`color: ${color}`);
    };

    const applyFontFamily = (family: string) => {
        if (!family) return;
        applySpanStyle(`font-family: ${family}`);
    };

    const applyFontSize = (size: string) => {
        if (!size) return;
        applySpanStyle(`font-size: ${size}`);
    };

    const applySpanStyle = (style: string) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        setError(null);

        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);
        if (range.collapsed) {
            setError('Selecione o texto para aplicar fonte ou tamanho.');
            return;
        }

        const span = document.createElement('span');
        span.setAttribute('style', style);
        try {
            range.surroundContents(span);
        } catch {
            const fragment = range.extractContents();
            span.appendChild(fragment);
            range.insertNode(span);
        }
        sel.removeAllRanges();
        emitChange();
    };

    const insertImageByUrl = (url: string) => {
        const trimmed = url.trim();
        if (!trimmed || !isValidLinkUrl(trimmed)) {
            setError('URL inválida. Use https://... ou um caminho começando com /');
            return;
        }
        setError(null);
        const src = escapeAttr(trimmed);
        insertHtml(
            `<figure class="news-figure"><img src="${src}" alt="" loading="lazy" /></figure><p><br></p>`
        );
    };

    const handleImageUrl = () => {
        const url = window.prompt('Cole a URL da imagem:', 'https://');
        if (!url?.trim() || url.trim() === 'https://') return;
        insertImageByUrl(url);
    };

    const handleInsertTable = () => {
        const colsStr = window.prompt('Quantas colunas?', '2');
        const rowsStr = window.prompt('Quantas linhas (incluindo cabeçalho)?', '4');
        const cols = Math.min(10, Math.max(1, parseInt(colsStr || '2', 10) || 2));
        const rows = Math.min(30, Math.max(2, parseInt(rowsStr || '4', 10) || 4));

        let html = '<table class="news-table"><thead><tr>';
        for (let c = 0; c < cols; c++) {
            html += `<th>Coluna ${c + 1}</th>`;
        }
        html += '</tr></thead><tbody>';
        for (let r = 1; r < rows; r++) {
            html += '<tr>';
            for (let c = 0; c < cols; c++) {
                html += '<td>&nbsp;</td>';
            }
            html += '</tr>';
        }
        html += '</tbody></table><p><br></p>';
        insertHtml(html);
    };

    const uploadAndInsertImage = async (file: File) => {
        setUploading(true);
        setError(null);
        try {
            const result = await uploadService.uploadImage(file);
            if (!result.success || !result.url) {
                setError('Falha ao enviar imagem.');
                return;
            }
            const src = resolvePublicMediaUrl(result.url) ?? result.url;
            insertHtml(
                `<figure class="news-figure"><img src="${src}" alt="" loading="lazy" /></figure><p><br></p>`
            );
        } catch {
            setError('Falha ao enviar imagem.');
        } finally {
            setUploading(false);
        }
    };

    const uploadAndInsertVideo = async (file: File) => {
        setUploading(true);
        setError(null);
        try {
            const result = await uploadService.uploadVideo(file);
            if (!result.success || !result.url) {
                setError('Falha ao enviar vídeo.');
                return;
            }
            const src = resolvePublicMediaUrl(result.url) ?? result.url;
            insertHtml(
                `<div class="news-video-file"><video src="${src}" controls preload="metadata"></video></div><p><br></p>`
            );
        } catch {
            setError('Falha ao enviar vídeo.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="rich-news-editor">
            <div className="rich-news-toolbar" role="toolbar" aria-label="Formatação do texto">
                <button type="button" title="Negrito" onClick={() => exec('bold')}>
                    <strong>B</strong>
                </button>
                <button type="button" title="Itálico" onClick={() => exec('italic')}>
                    <em>I</em>
                </button>
                <button type="button" title="Sublinhado" onClick={() => exec('underline')}>
                    <span className="rich-u">U</span>
                </button>
                <span className="rich-news-sep" />
                <button type="button" title="Título" onClick={() => exec('formatBlock', 'h2')}>
                    H2
                </button>
                <button type="button" title="Subtítulo" onClick={() => exec('formatBlock', 'h3')}>
                    H3
                </button>
                <span className="rich-news-sep" />
                <button type="button" title="Lista" onClick={() => exec('insertUnorderedList')}>
                    • Lista
                </button>
                <button type="button" title="Lista numerada" onClick={() => exec('insertOrderedList')}>
                    1. Lista
                </button>
                <button type="button" title="Citação" onClick={() => exec('formatBlock', 'blockquote')}>
                    “
                </button>
                <button type="button" title="Inserir tabela" onClick={handleInsertTable}>
                    Tabela
                </button>
                <span className="rich-news-sep" />
                <label className="rich-news-select-wrap" title="Fonte do texto selecionado">
                    <span>Fonte</span>
                    <select
                        defaultValue=""
                        onChange={(e) => {
                            applyFontFamily(e.target.value);
                            e.target.selectedIndex = 0;
                        }}
                    >
                        <option value="" disabled>Fonte...</option>
                        {FONT_OPTIONS.map((f) => (
                            <option key={f.label} value={f.value}>{f.label}</option>
                        ))}
                    </select>
                </label>
                <label className="rich-news-select-wrap" title="Tamanho do texto selecionado">
                    <span>Tamanho</span>
                    <select
                        defaultValue=""
                        onChange={(e) => {
                            applyFontSize(e.target.value);
                            e.target.selectedIndex = 0;
                        }}
                    >
                        <option value="" disabled>Tamanho...</option>
                        {SIZE_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </select>
                </label>
                <span className="rich-news-sep" />
                <span className="rich-news-color-group" title="Cor do texto">
                    {TEXT_COLORS.map((c) => (
                        <button
                            key={c.value}
                            type="button"
                            className="rich-news-color-swatch"
                            title={c.label}
                            style={{ backgroundColor: c.value }}
                            onClick={() => applyTextColor(c.value)}
                        />
                    ))}
                    <label className="rich-news-color-picker" title="Cor personalizada">
                        <span>A</span>
                        <input
                            type="color"
                            defaultValue="#d4af37"
                            onChange={(e) => applyTextColor(e.target.value)}
                        />
                    </label>
                </span>
                <span className="rich-news-sep" />
                <button type="button" title="Link no texto" onClick={handleLink}>
                    Link
                </button>
                <button type="button" title="Botão com link (download, mirror...)" onClick={handleDownloadButton}>
                    Botão
                </button>
                <button type="button" title="Mais um botão na mesma linha" onClick={handleDownloadButtonRow}>
                    + Botão
                </button>
                <button
                    type="button"
                    title="Enviar imagem do computador"
                    disabled={uploading}
                    onClick={() => imageInputRef.current?.click()}
                >
                    Imagem
                </button>
                <button
                    type="button"
                    title="Inserir imagem por URL"
                    disabled={uploading}
                    onClick={handleImageUrl}
                >
                    Imagem URL
                </button>
                <button
                    type="button"
                    title="Enviar vídeo (MP4/WebM)"
                    disabled={uploading}
                    onClick={() => videoInputRef.current?.click()}
                >
                    Vídeo
                </button>
                <button type="button" title="YouTube" onClick={handleYoutube}>
                    YouTube
                </button>
            </div>

            <div
                ref={editorRef}
                className="rich-news-body"
                contentEditable
                role="textbox"
                aria-multiline
                data-placeholder={placeholder}
                onInput={emitChange}
                onBlur={emitChange}
                suppressContentEditableWarning
            />

            {uploading && <p className="rich-news-hint">Enviando arquivo...</p>}
            {error && <p className="rich-news-error">{error}</p>}

            <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                hidden
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void uploadAndInsertImage(f);
                    e.target.value = '';
                }}
            />
            <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/webm"
                hidden
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void uploadAndInsertVideo(f);
                    e.target.value = '';
                }}
            />
        </div>
    );
};

export default RichNewsEditor;
